// web-frontend/src/components/common/YearConsentModal.jsx
/**
 * 그해 첫 로그인 동의 다시 확인
 *
 * 🔥 [2026-10-23 신규]
 * - 계정은 해마다 이어 쓰고, 참가·동의는 연도별 (user_event_participations)
 * - 이미 신청한 참가자의 그해 동의를 확인한다. 과거 기록 조회에는 강제하지 않는다.
 * - 미신청 바이어·투자자는 신청 버튼으로 열고 동의를 제출해야 올해 신청이 확정된다.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import apiClient from '../../shared/services/apiClient';
import useAuthStore from '../../shared/stores/authStore';
import i18nService from '../../shared/services/i18n';
import TermsModal from './TermsModal';
import { TERMS_CONTENT, PRIVACY_CONTENT } from '../../shared/constants/legalContent';
import './YearConsentModal.css';
import { historyText, pastRecordsLabel } from '../../shared/utils/participantHistoryText';

// RegisterPage 와 같은 동의서 버전 — legalContent 개정 때 함께 바꾼다
const CONSENT_VERSION = '2026-06-15';
const SKIP_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/change-password'];
const BUSINESS_TYPES = ['BUYER', 'INVESTOR', 'BUSINESS_VISITOR', 'COMPANY'];

const t = (key, params) => i18nService.t(`year_consent.${key}`, params || {});

const YearConsentModal = () => {
  const { user, isAuthenticated, logout } = useAuthStore();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null); // { year, userType }
  const [checks, setChecks] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(null); // 'terms' | 'privacy'

  const userId = user?.id;
  useEffect(() => {
    let cancelled = false;
    setInfo(null);
    if (!isAuthenticated || !userId) return undefined;
    apiClient.get('/auth/me').then((me) => {
      if (cancelled) return;
      useAuthStore.getState().updateUser(me);
      if (!me?.year_consent_required) return;
      setChecks({
        terms: false, privacy: false, thirdParty: false,
        sms: !!me.sms_consent, email: !!me.email_consent, night: !!me.marketing_night_consent,
      });
      setInfo({ year: me.event_year, userType: String(me.user_type || '').toUpperCase() });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthenticated, userId]);

  // 올해 신청은 사용자가 신청 버튼을 누른 뒤 동의를 제출할 때만 확정한다.
  useEffect(() => {
    const apply = async () => {
      if (!isAuthenticated || !userId) return;
      try {
        const me = await apiClient.get('/auth/me');
        if (!['BUYER', 'INVESTOR', 'BUSINESS_VISITOR'].includes(String(me.user_type).toUpperCase())) return;
        setChecks({ terms: false, privacy: false, thirdParty: false,
          sms: !!me.sms_consent, email: !!me.email_consent, night: !!me.marketing_night_consent });
        setInfo({ year: me.event_year, userType: String(me.user_type).toUpperCase(), applying: true });
      } catch { setError(t('save_failed')); }
    };
    window.addEventListener('participant-apply', apply);
    return () => window.removeEventListener('participant-apply', apply);
  }, [isAuthenticated, userId]);

  if (!info || SKIP_PATHS.some((p) => pathname.startsWith(p)) || (pathname.startsWith('/records') && !info.applying)) return null;

  const needThirdParty = BUSINESS_TYPES.includes(info.userType);
  const items = [
    { key: 'terms', label: t('terms'), required: true, view: 'terms' },
    { key: 'privacy', label: t('privacy'), required: true, view: 'privacy' },
    ...(needThirdParty ? [{ key: 'thirdParty', label: t('third_party'), required: true, info: t('third_party_info') }] : []),
    { key: 'sms', label: t('sms') },
    { key: 'email', label: t('email') },
    { key: 'night', label: t('night'), disabled: !checks.sms },
  ];
  const allChecked = items.every((it) => it.disabled || checks[it.key]);
  const requiredOk = items.filter((it) => it.required).every((it) => checks[it.key]);

  const toggle = (key) => setChecks((c) => {
    const next = { ...c, [key]: !c[key] };
    if (key === 'sms' && !next.sms) next.night = false; // 야간은 SMS 동의 전제
    return next;
  });
  const toggleAll = () => {
    const v = !allChecked;
    setChecks({ terms: v, privacy: v, thirdParty: v, sms: v, email: v, night: v });
  };

  const submit = async () => {
    if (!requiredOk) { setError(t('required_missing')); return; }
    setSaving(true);
    setError('');
    try {
      await apiClient.post('/auth/year-consent', {
        terms_consent: true,
        privacy_consent: true,
        third_party_consent: needThirdParty ? true : !!checks.thirdParty,
        sms_consent: !!checks.sms,
        email_consent: !!checks.email,
        marketing_night_consent: !!(checks.sms && checks.night),
        consent_version: CONSENT_VERSION,
      });
      setInfo(null);
      useAuthStore.getState().updateUser(await apiClient.get('/auth/me'));
      window.dispatchEvent(new Event('participant-applied'));
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || t('save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const lang = i18nService.currentLanguage;
  return (
    <div className="year-consent-overlay">
      <div className="year-consent-container" role="dialog" aria-modal="true">
        <div className="year-consent-icon"><ShieldCheck size={30} /></div>
        <h3 className="year-consent-title">{info.applying ? `${info.year} · ${historyText(i18nService.currentLanguage).apply}` : t('title', { year: info.year })}</h3>
        <p className="year-consent-message">{t('message', { year: info.year })}</p>

        <label className="year-consent-row all">
          <input type="checkbox" checked={allChecked} onChange={toggleAll} />
          <span>{t('all')}</span>
        </label>
        <div className="year-consent-list">
          {items.map((it) => (
            <div key={it.key} className={`year-consent-item${it.disabled ? ' disabled' : ''}`}>
              <label className="year-consent-row">
                <input type="checkbox" checked={!!checks[it.key]} disabled={it.disabled} onChange={() => toggle(it.key)} />
                <span>{it.label}</span>
                {it.view && (
                  <button type="button" className="year-consent-view" onClick={(e) => { e.preventDefault(); setViewing(it.view); }}>
                    {t('view')}
                  </button>
                )}
              </label>
              {it.info && <p className="year-consent-info">{it.info}</p>}
            </div>
          ))}
        </div>
        <p className="year-consent-hint">{t('hint')}</p>
        {error && <p className="year-consent-error">{error}</p>}

        <div className="year-consent-actions">
          <button type="button" className="year-consent-btn secondary" onClick={() => logout()} disabled={saving}>
            {t('logout')}
          </button>
          <button type="button" className="year-consent-btn primary" onClick={submit} disabled={saving || !requiredOk}>
            {saving ? <span className="loading-spinner" /> : t('submit')}
          </button>
        </div>
        {!info.applying && BUSINESS_TYPES.includes(info.userType) && <button type="button"
          className="year-consent-btn secondary" style={{ width: '100%', marginTop: 12 }} disabled={saving}
          onClick={() => navigate('/records')}>{pastRecordsLabel(i18nService.currentLanguage)}</button>}
      </div>

      <TermsModal
        isOpen={viewing === 'terms'}
        onClose={() => setViewing(null)}
        onConfirm={() => setChecks((c) => ({ ...c, terms: true }))}
        title={t('terms_title')}
        content={TERMS_CONTENT[lang] || TERMS_CONTENT.ko}
        confirmText={t('confirm_agree')}
        closeText={t('close')}
      />
      <TermsModal
        isOpen={viewing === 'privacy'}
        onClose={() => setViewing(null)}
        onConfirm={() => setChecks((c) => ({ ...c, privacy: true }))}
        title={t('privacy_title')}
        content={PRIVACY_CONTENT[lang] || PRIVACY_CONTENT.ko}
        confirmText={t('confirm_agree')}
        closeText={t('close')}
      />
    </div>
  );
};

export default YearConsentModal;
